import 'tippy.js/dist/tippy.css';
import {
  AdminSidebarBottom,
  AdminSidebarChurchDropdown,
  AdminSidebarLink,
  AdminSidebarLogoWrap,
  AdminSidebarNavTitle,
  AdminSidebarPrimary,
  AdminSidebarRail,
  AdminSidebarSection,
  AdminSidebarToggle,
} from './AdminSidebar.styled';

import React, { useEffect, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { css, Global } from '@emotion/react';
import Icon from '@OnArk/SVGs/Icon';
import Logo from '@OnArk/SVGs/Logo';
import posthog from '@OnArk/utils/posthog';
import { useSidebarState } from '@OnArk/utils/sidebarDefault';

export interface AdminSidebarChurch {
  icon?: string | null;
  id: string;
  name: string;
}

export interface AdminSidebarUser {
  churches: { church: AdminSidebarChurch }[];
  firstName?: string | null;
  lastName?: string | null;
  super?: boolean | null;
}

/**
 * Tippy ships unstyled into the App Router (only pages/_app imported its
 * CSS) — the import above + this override give the rail ink-on-white
 * tooltips on BOTH routers.
 */
const tooltipStyles = css`
  .tippy-box {
    background-color: rgba(15, 11, 26, 1);
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.3px;
  }
  .tippy-box > .tippy-content {
    padding: 5px 10px;
  }
  .tippy-box > .tippy-arrow {
    color: rgba(15, 11, 26, 1);
  }
`;

/** Below 992px the rail is CSS-forced condensed regardless of state. */
function useForcedCondensed(): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 991px)');
    const update = () => setNarrow(mq.matches);

    update();
    mq.addEventListener('change', update);

    return () => mq.removeEventListener('change', update);
  }, []);

  return narrow;
}

/**
 * Shared admin CondensableSidebar — **px-sized**, works on pages (10px rem)
 * and App Router (16px rem) without zoom / html font-size bridges.
 *
 * Data wrappers: pages `AdminNav` (GraphQL), App `LegacyAdminChrome` (tRPC).
 */
export default function AdminSidebar({
  asPath,
  church,
  onChangeChurch,
  onLogout,
  user,
}: {
  asPath: string;
  church: AdminSidebarChurch;
  onChangeChurch: (churchId: string) => void | Promise<void>;
  onLogout: (e?: React.MouseEvent) => void;
  user: AdminSidebarUser;
}) {
  const [churchDropdown, setChurchDropdown] = useState(false);
  const [sidebarState, setSidebarState] = useSidebarState(
    'onark-main-sidebar-state'
  );
  const forcedCondensed = useForcedCondensed();
  const condensed = sidebarState === 'CONDENSED';
  // Tooltips only when the rail is icon-only — labeled items don't need them.
  const iconRail = condensed || forcedCondensed;
  const tip = (label: string) => (iconRail ? label : undefined);

  useEffect(() => {
    setChurchDropdown(false);
  }, [asPath]);

  return (
    <AdminSidebarRail
      condensed={sidebarState === 'CONDENSED'}
      data-testid="admin-sidebar"
    >
      <Global styles={tooltipStyles} />
      <aside>
        <header>
          <AdminSidebarToggle
            type="button"
            onClick={e => {
              setSidebarState(sidebarState === 'OPEN' ? 'CONDENSED' : 'OPEN');
              e.preventDefault();
              e.stopPropagation();
              posthog?.capture('setSideBarStateAdminNav', { sidebarState });
            }}
          >
            <Icon
              distance={-45}
              name={condensed ? 'ARROWS_Right' : 'ARROWS_Left'}
              right="0"
              size="15"
            />
          </AdminSidebarToggle>
          <AdminSidebarChurchDropdown open={churchDropdown}>
            <figure
              aria-hidden
              onClick={() => setChurchDropdown(!churchDropdown)}
            />
            <a
              role="button"
              tabIndex={0}
              onClick={() => setChurchDropdown(!churchDropdown)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setChurchDropdown(!churchDropdown);
                }
              }}
            >
              <circle>
                {church.icon ? (
                  <Image
                    alt={church.name}
                    height={27}
                    src={process.env.NEXT_PUBLIC_ASSET_URL + church.icon}
                    width={27}
                  />
                ) : (
                  <span>{church.name?.[0]}</span>
                )}
              </circle>
              <small>{church.name}</small>
              <Icon
                name={churchDropdown ? 'ARROWS_Up' : 'ARROWS_Down'}
                right="0"
                size="15"
              />
            </a>
            <nav>
              {user.churches.map(({ church: c }) => (
                <a
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    void onChangeChurch(c.id);
                    posthog?.capture('switchChurches', church);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void onChangeChurch(c.id);
                      posthog?.capture('switchChurches', church);
                    }
                  }}
                >
                  <circle>
                    {c.icon && (
                      <Image
                        alt={`${c.name} Logo`}
                        height={27}
                        src={process.env.NEXT_PUBLIC_ASSET_URL + c.icon}
                        width={27}
                      />
                    )}
                    {!c.icon && <span>{c.name[0]}</span>}
                  </circle>
                  <small>{c.name}</small>
                </a>
              ))}
              <Link passHref href="/admin/create-account">
                <circle>
                  <span>+</span>
                </circle>
                <small>Add Church</small>
              </Link>
            </nav>
          </AdminSidebarChurchDropdown>
        </header>

        <main>
          <section>
            <AdminSidebarPrimary>
              <AdminSidebarSection>
                <AdminSidebarLink href="/admin/classes">
                  <Icon
                    distance={-45}
                    name="ADMIN_NAV_Classes"
                    right="0"
                    size="30"
                    tooltip={tip('Class Directory')}
                  />
                  Class Directory
                </AdminSidebarLink>
                <AdminSidebarLink href="/admin/guests">
                  <Icon
                    distance={-45}
                    name="ADMIN_NAV_Guests"
                    right="0"
                    size="30"
                    tooltip={tip('Guests')}
                  />
                  Guests
                </AdminSidebarLink>
                <AdminSidebarLink href="/admin/reports">
                  <Icon
                    distance={-45}
                    name="ADMIN_NAV_Reports"
                    right="0"
                    size="30"
                    tooltip={tip('Reports')}
                  />
                  Reports
                </AdminSidebarLink>
                <AdminSidebarLink href="/admin/resources">
                  <Icon
                    distance={-45}
                    name="ADMIN_NAV_Resources"
                    right="0"
                    size="30"
                    tooltip="Resources"
                  />
                  Resources
                </AdminSidebarLink>
              </AdminSidebarSection>
            </AdminSidebarPrimary>
            <AdminSidebarSection>
              <AdminSidebarNavTitle>Support</AdminSidebarNavTitle>
              <AdminSidebarLink href="/docs" target="blank">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_SupportDocs"
                  right="0"
                  size="30"
                  tooltip={tip('Help Docs')}
                />
                Help Docs
              </AdminSidebarLink>
              <AdminSidebarLink href="/contact" target="blank">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Bug"
                  right="0"
                  size="30"
                  tooltip={tip('Report Bug')}
                />
                Report Bug
              </AdminSidebarLink>

              <AdminSidebarNavTitle>{church.name}</AdminSidebarNavTitle>
              <AdminSidebarLink href="/admin/team">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Team"
                  right="0"
                  size="30"
                  tooltip={tip('Team')}
                />
                Team
              </AdminSidebarLink>
              <AdminSidebarLink href="/admin/ministries">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Minstries"
                  right="0"
                  size="30"
                  tooltip={tip('Ministries')}
                />
                Ministries
              </AdminSidebarLink>
              <AdminSidebarLink href="/admin/settings/branding">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Branding"
                  right="0"
                  size="30"
                  tooltip={tip('Branding')}
                />
                Branding
              </AdminSidebarLink>
              <AdminSidebarLink href="/admin/settings/church">
                <Icon
                  distance={-45}
                  name="ACTIONS_Settings"
                  right="0"
                  size="30"
                  tooltip={tip('Church')}
                />
                Church
              </AdminSidebarLink>
              <AdminSidebarLink href="/admin/billing">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Billing"
                  right="0"
                  size="30"
                  tooltip={tip('Billing')}
                />
                Billing
              </AdminSidebarLink>
            </AdminSidebarSection>

            {/* Account cluster — pinned to the rail bottom in both states. */}
            <AdminSidebarBottom>
              <AdminSidebarNavTitle>
                {user.firstName} {user.lastName}
              </AdminSidebarNavTitle>
              <AdminSidebarLink href="/admin/profile">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Profile"
                  right="0"
                  size="30"
                  tooltip={tip('Profile')}
                />
                Profile
              </AdminSidebarLink>
              <AdminSidebarLink href="/admin/referrals">
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Refer"
                  right="0"
                  size="30"
                  tooltip={tip('Refer a Church')}
                />
                Refer a Church
              </AdminSidebarLink>

              {/* No `as` here: AdminSidebarLink wraps next/link, where `as` is
                  the legacy URL-mask prop — `as="button"` rendered
                  href="button", a relative URL the App Router then prefetched
                  (/admin/settings/button → 307), wedging networkidle on
                  previews. prefetch={false}: logout must never be prefetched. */}
              <AdminSidebarLink
                href="/login"
                prefetch={false}
                onClick={e => void onLogout(e)}
              >
                <Icon
                  distance={-45}
                  name="ADMIN_NAV_Logout"
                  right="0"
                  size="30"
                  tooltip={tip('Log Out')}
                />
                Log Out
              </AdminSidebarLink>
            </AdminSidebarBottom>
          </section>
        </main>
        <footer>
          <AdminSidebarLogoWrap>
            <Logo hideName={condensed} />
          </AdminSidebarLogoWrap>
        </footer>
      </aside>
    </AdminSidebarRail>
  );
}
